import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { worklistService, } from "@/services/worklistService";
import { orderInfoService } from "@/services/orderInfoService";
import { intakeService } from "@/services/intakeService";
import { lockFields } from "@/composables/usePATable";
import { useActionNeededPref } from "@/composables/useActionNeededPref";
import { createLockPollHalt, LOCK_POLL_INTERVAL_MS } from "@/utils/lockPoll";
import { useIntervalWhenVisible } from "@/composables/useIntervalWhenVisible";
import { SCOPE } from "@/constants/scope";
import { audienceFor, companyAudienceFor } from "@/constants/worklistSections";
import { WorklistRow } from "@/models/worklistRow";
import { queryValues } from "@/models/listFilters";
import { useActionNeededStore, useRequirementsStore } from "@/stores";
export const FILTER_KEYS = [
    "pa_status",
    "intake_status",
    "type",
    "tags",
    "tags_match",
    "due_from",
    "due_to",
    "substatus",
    "substatus_match",
    "assigned_user",
    "assigned_group",
    "mentioned_user",
    "modified_by",
    "document_type",
    "sent_by",
    "date_from",
    "date_to",
    "sent_from",
    "sent_to",
    "search",
];
const REPEATED_FILTER_KEYS = ["modified_by", "document_type", "sent_by"];
const REPEATED_KEYS = new Set(REPEATED_FILTER_KEYS);
const LIST_KEYS = [...FILTER_KEYS, "sort", "direction", "page", "page_size"];
const STATUS_KEYS = ["pa_status", "intake_status"];
// The views a URL may carry: `snoozed` as a status pick, `all` as a section's opt-out of Action Needed.
const URL_VIEWS = ["all", "snoozed"];
const ACTION_NEEDED_KEY = "lh_worklist_action_needed";
const ASC_FIRST_SORTS = new Set(["tag_due", "substatus"]);
function pick(query, keys) {
    const picked = {};
    for (const key of keys) {
        const value = query[key];
        if (REPEATED_KEYS.has(key)) {
            const values = queryValues(value);
            if (values.length)
                picked[key] = values;
        }
        else if (typeof value === "string" && value !== "")
            picked[key] = value;
    }
    return picked;
}
function readStoredFlag() {
    try {
        return localStorage.getItem(ACTION_NEEDED_KEY) !== "0";
    }
    catch {
        return true;
    }
}
function storeFlag(on) {
    try {
        localStorage.setItem(ACTION_NEEDED_KEY, on ? "1" : "0");
    }
    catch {
        // Storage may be unavailable (private window); the chip still works for this visit.
    }
}
const PAGE_CACHE = new Map();
const PAGE_CACHE_LIMIT = 20;
const PREFETCHING = new Set();
let cacheRevision = 0;
function actionNeededRevision() {
    return useActionNeededStore().revision;
}
/** The page cache, emptied once the user's Action Needed lists change: the server filtered its pages by them. */
function pageCache() {
    const revision = actionNeededRevision();
    if (revision !== cacheRevision) {
        PAGE_CACHE.clear();
        cacheRevision = revision;
    }
    return PAGE_CACHE;
}
function cachePut(key, page) {
    const cache = pageCache();
    cache.delete(key);
    cache.set(key, page);
    if (cache.size > PAGE_CACHE_LIMIT)
        cache.delete(cache.keys().next().value);
}
function viewFromQuery(query) {
    const raw = query.view;
    return typeof raw === "string" && URL_VIEWS.includes(raw) ? raw : null;
}
function hasStatusIn(query) {
    return STATUS_KEYS.some((key) => typeof query[key] === "string" && query[key] !== "");
}
/** The list request a Worklist URL makes: its filters plus the view its chips and status pick. */
function listParamsFor(query, audience, actionNeeded) {
    const view = viewFromQuery(query) ?? (actionNeeded && !hasStatusIn(query) ? "action_needed" : "all");
    return { ...pick(query, LIST_KEYS), view };
}
/** Load a Worklist URL into the page cache, so opening it from the sidebar paints at once. */
export function prefetchWorklist(query) {
    const company = companyAudienceFor(useRequirementsStore().visibleModules ?? []);
    const audience = audienceFor(company, query.type);
    const chips = useActionNeededStore().showChip;
    const actionNeeded = audience === "prior_auth" ? chips[SCOPE.PA] : audience === "intake" ? chips[SCOPE.INTAKE] : readStoredFlag();
    const params = listParamsFor(query, audience, actionNeeded !== false);
    const key = JSON.stringify(params);
    if (pageCache().has(key) || PREFETCHING.has(key))
        return;
    PREFETCHING.add(key);
    const revision = actionNeededRevision();
    worklistService
        .list(params)
        .then((response) => {
        if (actionNeededRevision() === revision)
            cachePut(key, response);
    })
        .catch(() => { })
        .finally(() => PREFETCHING.delete(key));
}
function isCancel(error) {
    const code = error?.code;
    const inner = error?.error?.code;
    return code === "ERR_CANCELED" || inner === "ERR_CANCELED";
}
export function useWorklistTable() {
    const route = useRoute();
    const router = useRouter();
    const requirementsStore = useRequirementsStore();
    const rows = ref([]);
    const totalCount = ref(0);
    const counts = ref({});
    const view = ref("all");
    const sort = ref("created");
    const direction = ref("desc");
    const responseModules = ref([]);
    const loading = ref(false);
    const firstLoad = ref(true);
    const error = ref(null);
    const notEnabled = ref(false);
    const lockPollError = ref(null);
    let listController = null;
    let countsController = null;
    let pendingFilters = null;
    let listReady = false;
    let holdList = false;
    let requestedKey = null;
    let shownKey = null;
    let unmounted = false;
    const lockPoll = createLockPollHalt(lockPollError);
    const visibleModules = computed(() => {
        const live = requirementsStore.visibleModules;
        return live?.length ? live : responseModules.value;
    });
    const companyAudience = computed(() => companyAudienceFor(visibleModules.value));
    const audience = computed(() => audienceFor(companyAudience.value, route.query.type));
    const displayAudience = ref(audience.value);
    const displayQuery = ref({ ...route.query });
    const paActionNeeded = useActionNeededPref(SCOPE.PA);
    const intakeActionNeeded = useActionNeededPref(SCOPE.INTAKE);
    const worklistActionNeeded = ref(readStoredFlag());
    const actionNeeded = computed({
        get: () => {
            if (audience.value === "prior_auth")
                return paActionNeeded.value;
            if (audience.value === "intake")
                return intakeActionNeeded.value;
            return worklistActionNeeded.value;
        },
        set: (on) => {
            if (audience.value === "prior_auth")
                paActionNeeded.value = on;
            else if (audience.value === "intake")
                intakeActionNeeded.value = on;
            else {
                worklistActionNeeded.value = on;
                storeFlag(on);
            }
        },
    });
    const urlView = computed(() => viewFromQuery(route.query));
    const filterParams = computed(() => pick(route.query, FILTER_KEYS));
    const actionNeededActive = computed(() => actionNeeded.value && !urlView.value && !hasStatusIn(route.query));
    const listParams = computed(() => listParamsFor(route.query, audience.value, actionNeeded.value));
    const listKey = computed(() => JSON.stringify(listParams.value));
    const countParams = computed(() => {
        const params = { ...filterParams.value };
        for (const key of STATUS_KEYS)
            delete params[key];
        return params;
    });
    const filters = computed(() => filterParams.value);
    const page = computed(() => Number(route.query.page ?? 1) || 1);
    const pageSize = computed(() => Number(route.query.page_size ?? 25) || 25);
    const activeFilterCount = computed(() => Object.keys(filterParams.value).filter((key) => key !== "search" && key !== "type").length +
        (urlView.value && urlView.value !== "all" ? 1 : 0));
    function apply(response, query, key) {
        const shownAs = audienceFor(companyAudienceFor(response.visibleModules), query.type);
        shownKey = key;
        displayAudience.value = shownAs;
        displayQuery.value = query;
        const perspective = shownAs === "intake" ? "intake" : "case";
        const medicalNecessity = response.visibleModules.includes("clinical_review");
        rows.value = response.results.map((raw) => new WorklistRow(raw, perspective, medicalNecessity));
        totalCount.value = response.count;
        view.value = response.view;
        sort.value = response.sort;
        direction.value = response.direction;
        responseModules.value = response.visibleModules;
        if (response.page && response.page !== (Number(query.page ?? 1) || 1)) {
            const served = JSON.parse(key);
            if (response.page > 1)
                served.page = String(response.page);
            else
                delete served.page;
            shownKey = JSON.stringify(served);
            cachePut(shownKey, response);
            navigate({ page: response.page > 1 ? String(response.page) : undefined }, { replace: true });
        }
    }
    async function fetchRows({ silent = false } = {}) {
        listController?.abort();
        const controller = new AbortController();
        listController = controller;
        const params = listParams.value;
        const query = { ...route.query };
        const key = JSON.stringify(params);
        requestedKey = key;
        const cached = pageCache().get(key);
        if (cached && key !== shownKey) {
            apply(cached, query, key);
            firstLoad.value = false;
        }
        loading.value = !silent && !cached;
        error.value = null;
        try {
            const response = await worklistService.list(params, controller.signal);
            if (controller.signal.aborted)
                return;
            apply(response, query, key);
            cachePut(key, response);
        }
        catch (err) {
            if (isCancel(err))
                return;
            if (err?.response?.status === 404)
                notEnabled.value = true;
            else
                error.value = "The Worklist could not load. Try again.";
        }
        finally {
            if (listController === controller) {
                loading.value = false;
                firstLoad.value = false;
            }
        }
    }
    async function fetchCounts() {
        countsController?.abort();
        const controller = new AbortController();
        countsController = controller;
        try {
            counts.value = await worklistService.counts(countParams.value, controller.signal);
        }
        catch (err) {
            if (!isCancel(err))
                counts.value = {};
        }
    }
    async function fetchLocks() {
        const shown = rows.value;
        if (lockPoll.isHalted() || !shown.length)
            return;
        const paKeys = shown.flatMap((row) => (row.pa ? [String(row.pa.id)] : []));
        const docKeys = shown.flatMap((row) => (row.document ? [String(row.document.id)] : []));
        try {
            const [paLocks, docLocks] = (await Promise.all([
                paKeys.length ? orderInfoService.getSubmissionLocks(paKeys) : Promise.resolve({ locks: [] }),
                docKeys.length ? intakeService.getDocumentLocks(docKeys) : Promise.resolve({ locks: [] }),
            ]));
            if (rows.value !== shown)
                return;
            if (!Array.isArray(paLocks?.locks) || !Array.isArray(docLocks?.locks)) {
                lockPoll.halt();
                return;
            }
            const paHeld = new Map(paLocks.locks.map((lock) => [lock.key, lock]));
            const docHeld = new Map(docLocks.locks.map((lock) => [lock.key, lock]));
            for (const row of shown) {
                if (row.pa)
                    Object.assign(row.pa, lockFields(paHeld.get(String(row.pa.id))));
                if (row.document)
                    row.document.applyLock(docHeld.get(String(row.document.id)));
            }
        }
        catch (err) {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500)
                lockPoll.halt();
        }
    }
    function navigate(next, { replace = false, keepPage = false } = {}) {
        const query = { ...pick(route.query, [...LIST_KEYS, "view"]) };
        for (const [key, value] of Object.entries(next)) {
            if (value === undefined || value.length === 0)
                delete query[key];
            else
                query[key] = value;
        }
        if (!keepPage && !("page" in next))
            delete query.page;
        const location = { name: route.name ?? undefined, query };
        return replace ? router.replace(location) : router.push(location);
    }
    /** Navigate, then set the Action Needed preference of the view navigated to; the list loads once for both. */
    async function navigateWithActionNeeded(next, on) {
        holdList = true;
        try {
            await navigate(next);
        }
        finally {
            holdList = false;
        }
        if (actionNeeded.value !== on)
            actionNeeded.value = on;
        if (listReady && listKey.value !== requestedKey)
            fetchRows();
    }
    function setFilter(key, value) {
        queuePatch({ [key]: value });
    }
    /** Clearing tags drops their match and due window, and a tag due date sort, as the PA tab does. */
    function clearTagFilter() {
        const sortPatch = route.query.sort === "tag_due" ? { sort: undefined, direction: undefined } : {};
        queuePatch({ tags: undefined, tags_match: undefined, due_from: undefined, due_to: undefined, ...sortPatch });
    }
    function queuePatch(patch) {
        const flushNow = pendingFilters === null;
        pendingFilters = { ...(pendingFilters ?? {}), ...patch };
        if (flushNow) {
            queueMicrotask(() => {
                const patch = pendingFilters ?? {};
                pendingFilters = null;
                navigate(patch);
            });
        }
    }
    /** Status picks, as on the tabs: a specific status or a Completed/Snoozed/All view turns Action Needed off. */
    function setStatus(next) {
        const query = {
            view: next.view,
            pa_status: next.paStatus,
            intake_status: next.intakeStatus,
            sort: undefined,
            direction: undefined,
        };
        if (next.view || next.paStatus || next.intakeStatus)
            return navigateWithActionNeeded(query, false);
        return navigate(query);
    }
    /** Narrow to work types (the module chips), keeping search, people, dates, tags and the statuses that still apply. */
    function setTypes(types) {
        const type = types.join(",") || undefined;
        const next = audienceFor(companyAudience.value, type);
        const appealsOnly = types.length === 1 && types[0] === "appeals";
        return navigate({
            type,
            ...(appealsOnly ? {} : { sent_from: undefined, sent_to: undefined }),
            ...(next === "intake" ? { pa_status: undefined } : {}),
            ...(next === "prior_auth" ? { intake_status: undefined, view: undefined } : {}),
            sort: undefined,
            direction: undefined,
        });
    }
    function toggleActionNeeded() {
        return navigateWithActionNeeded({ view: undefined, pa_status: undefined, intake_status: undefined, sort: undefined, direction: undefined }, !actionNeededActive.value);
    }
    function toggleAssignedToMe() {
        if (filterParams.value.assigned_user === "me")
            return navigate({ assigned_user: undefined });
        return navigateWithActionNeeded({ assigned_user: "me", view: undefined, pa_status: undefined, intake_status: undefined }, false);
    }
    /** Assigned and Group picks, as on the tabs: picking someone turns Action Needed off, a group does not. */
    function setAssignment(key, value) {
        if (!value || key === "assigned_group")
            return navigate({ [key]: value });
        return navigateWithActionNeeded({ [key]: value }, false);
    }
    function setSearch(value) {
        const search = value.trim() || undefined;
        if (search === filterParams.value.search)
            return;
        return navigate({ search }, { replace: true });
    }
    function setSort(key, dir) {
        const toggled = direction.value === "desc" ? "asc" : "desc";
        const nextDir = dir ?? (sort.value === key ? toggled : ASC_FIRST_SORTS.has(key) ? "asc" : "desc");
        return navigate({ sort: key, direction: nextDir });
    }
    function setPage(next) {
        return navigate({ page: next > 1 ? String(next) : undefined }, { keepPage: true });
    }
    function clearFilters() {
        const cleared = FILTER_KEYS.filter((key) => key !== "type").map((key) => [key, undefined]);
        // `view=all` is a section's opt-out of Action Needed, not a filter, so clearing the filters keeps it.
        const view = urlView.value === "all" ? "all" : undefined;
        return navigateWithActionNeeded({ view, sort: undefined, direction: undefined, ...Object.fromEntries(cleared) }, true);
    }
    watch(listKey, (key) => {
        if (listReady && !holdList && key !== requestedKey)
            fetchRows();
    });
    watch(() => JSON.stringify(countParams.value), fetchCounts, { immediate: true });
    watch(actionNeededRevision, () => {
        fetchCounts();
        if (listReady)
            fetchRows();
    });
    useIntervalWhenVisible(fetchLocks, LOCK_POLL_INTERVAL_MS);
    useActionNeededStore()
        .ensureLoaded()
        .then(() => {
        if (unmounted)
            return;
        listReady = true;
        fetchRows();
    });
    onBeforeUnmount(() => {
        unmounted = true;
        listController?.abort();
        countsController?.abort();
    });
    return {
        rows,
        totalCount,
        counts,
        view,
        urlView,
        sort,
        direction,
        page,
        pageSize,
        filters,
        activeFilterCount,
        visibleModules,
        audience,
        companyAudience,
        displayAudience,
        displayQuery,
        actionNeededActive,
        loading,
        firstLoad,
        error,
        notEnabled,
        lockPollError,
        refresh: () => Promise.all([fetchRows(), fetchCounts()]),
        silentRefresh: () => Promise.all([fetchRows({ silent: true }), fetchCounts()]),
        setFilter,
        clearTagFilter,
        setStatus,
        setTypes,
        setAssignment,
        toggleActionNeeded,
        toggleAssignedToMe,
        setSearch,
        setSort,
        setPage,
        clearFilters,
    };
}
